// ProductionNotes: the card's attributed notes thread (D-131), on src/ui (HD-8). On the Production page it is the Notes
// panel at the right of the Workbench. Who, when and the text, newest at the bottom; the thread scrolls and the composer
// is pinned under it (Ctrl+Enter or Post) with a Hand-off switch; a Hand-off filter in the head. Your own notes get edit
// and delete; Admin and Power User can delete any. No notifications.
// Same calls as before: GET /api/live-production/:id/notes, POST …/notes { body, isHandoff },
// PATCH …/notes/:noteId { body } | { isHandoff }, DELETE …/notes/:noteId (now behind a two-step confirm).
// Shortcuts kept: Ctrl/Cmd+Enter posts; in a note being edited, Ctrl/Cmd+Enter saves and Escape cancels.
import { useState, useEffect, useRef, useCallback } from 'react';
import { Flag, FlagOff, Pencil, Trash2 } from 'lucide-react';
import api from '../lib/api';
import Avatar from '../ui/Avatar';
import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import Tag from '../ui/Tag';
import { useConfirm } from '../ui/Confirm';
import { clean } from '../shell/copy';

const ADMIN = ['Admin', 'Power User'];
function relTime(iso) {
  const t = new Date(iso).getTime(); const d = Date.now() - t;
  const m = Math.round(d / 60000); if (m < 1) return 'just now'; if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24); if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function ProductionNotes({ cardId, user }) {
  const confirm = useConfirm();
  const [notes, setNotes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState('');
  const [handoff, setHandoff] = useState(false);
  const [onlyHandoff, setOnlyHandoff] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // { id, value }
  const [error, setError] = useState(null);
  const listRef = useRef(null);
  const canAdmin = (user?.roles || []).some(r => ADMIN.includes(r));
  const load = useCallback(async () => { if (!cardId) return; try { const r = await api.get(`/api/live-production/${cardId}/notes`); setNotes(r.data.notes || []); } catch { /* the thread stays empty */ } finally { setLoaded(true); } }, [cardId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }, [notes.length, onlyHandoff]);
  async function post() {
    const body = draft.trim(); if (!body || busy) return;
    setBusy(true); setError(null);
    try { const r = await api.post(`/api/live-production/${cardId}/notes`, { body, isHandoff: handoff }); setNotes(n => [...n, r.data]); setDraft(''); setHandoff(false); }
    catch (e) { setError(e.response?.data?.error || 'The note was not posted. Try again.'); }
    finally { setBusy(false); }
  }
  async function saveEdit() {
    if (!editing) return; const body = editing.value.trim(); const cur = notes.find(n => n.id === editing.id);
    if (!cur || body === cur.body) { setEditing(null); return; }
    try { const r = await api.patch(`/api/live-production/${cardId}/notes/${editing.id}`, { body }); setNotes(n => n.map(x => x.id === r.data.id ? r.data : x)); }
    catch (e) { setError(e.response?.data?.error || 'The note was not saved. Try again.'); }
    setEditing(null);
  }
  async function toggleHandoff(note) {
    try { const r = await api.patch(`/api/live-production/${cardId}/notes/${note.id}`, { isHandoff: !note.isHandoff }); setNotes(n => n.map(x => x.id === r.data.id ? r.data : x)); }
    catch (e) { setError(e.response?.data?.error || 'The note did not change. Try again.'); }
  }
  async function remove(note) {
    const ok = await confirm({ title: 'Delete this note?', body: 'The note leaves the thread for everyone. This cannot be undone.', confirmLabel: 'Delete note', destructive: true });
    if (!ok) return;
    try { await api.delete(`/api/live-production/${cardId}/notes/${note.id}`); setNotes(n => n.filter(x => x.id !== note.id)); }
    catch (e) { setError(e.response?.data?.error || 'The note was not deleted. Try again.'); }
  }
  const shown = onlyHandoff ? notes.filter(n => n.isHandoff) : notes;
  const handoffCount = notes.filter(n => n.isHandoff).length;
  return (
    <div className="ui-chat" data-testid="production-notes" data-count={notes.length}>
      <div className="ui-drawer__head">
        <h2 className="ui-heading">Notes{notes.length ? <> <span className="ui-meta" data-testid="production-notes-count">{notes.length}</span></> : null}</h2>
        {handoffCount > 0 && (
          <Button variant="quiet" icon={Flag} data-testid="production-notes-filter" aria-pressed={onlyHandoff} onClick={() => setOnlyHandoff(v => !v)}
            title={onlyHandoff ? 'Showing hand-off notes only. Press for all notes.' : 'Show hand-off notes only'}>Hand-off {handoffCount}</Button>
        )}
      </div>
      <div className="ui-thread ui-thread--drawer ui-stack" ref={listRef} data-testid="production-notes-list">
        {loaded && shown.length === 0 && (
          <p className="ui-p ui-dense ui-text-3" data-testid="production-notes-empty">
            {notes.length ? 'No hand-off notes yet.' : 'No notes yet. Leave hand-off details, reminders or anything the next person should read.'}
          </p>
        )}
        {shown.map(n => {
          const mine = n.author?.id && n.author.id === user?.id;
          return (
            <article key={n.id} className="ui-note" data-testid="production-note" data-handoff={n.isHandoff ? 'true' : 'false'} data-mine={mine ? 'true' : 'false'}>
              {n.author ? <Avatar name={n.author.name} initials={n.author.initials} /> : <Avatar name="Unattributed" initials="?" />}
              <div className="ui-note__body">
                <div className="ui-cluster">
                  <span className="ui-dense" data-testid="production-note-author">{clean(n.author?.name) || 'Unattributed'}</span>
                  <span className="ui-meta" title={new Date(n.createdAt).toLocaleString()}>{relTime(n.createdAt)}{n.edited ? ', edited' : ''}</span>
                  {n.isHandoff && <span data-testid="production-note-handoff"><Tag>Hand-off</Tag></span>}
                  {n.migratedFrom === 'edit_notes' && <span data-testid="production-note-migrated"><Tag title="Carried over from the old Edit notes box">From edit notes</Tag></span>}
                </div>
                {editing?.id === n.id
                  ? <textarea autoFocus className="ui-input ui-textarea" rows={3} aria-label="Edit note" data-testid="production-note-edit-input" value={editing.value}
                      onChange={e => setEditing({ id: n.id, value: e.target.value })} onBlur={saveEdit}
                      onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } if (e.key === 'Escape') setEditing(null); }} />
                  : <p className="ui-p ui-dense ui-prewrap" data-testid="production-note-text">{n.body}</p>}
              </div>
              <span className="ui-note__acts">
                {mine && editing?.id !== n.id && <Button variant="quiet" iconOnly icon={Pencil} label="Edit note" data-testid="production-note-edit" onClick={() => setEditing({ id: n.id, value: n.body })} />}
                {(mine || canAdmin) && <Button variant="quiet" iconOnly icon={n.isHandoff ? FlagOff : Flag} label={n.isHandoff ? 'Unmark hand-off' : 'Mark as hand-off'} data-testid="production-note-handoff-toggle" onClick={() => toggleHandoff(n)} />}
                {(mine || canAdmin) && <Button variant="quiet" iconOnly icon={Trash2} label="Delete note" data-testid="production-note-delete" onClick={() => remove(n)} />}
              </span>
            </article>
          );
        })}
      </div>
      <div className="ui-composer">
        <textarea className="ui-input ui-textarea" aria-label="Add a note" data-testid="production-notes-input" placeholder="Add a note (Ctrl+Enter posts)" value={draft} rows={2}
          onChange={e => { setDraft(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(120, e.target.scrollHeight) + 'px'; }}
          onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); post(); } }} disabled={busy} />
        <div className="ui-composer__tools">
          <span data-testid="production-notes-handoff" title="Tag this note as a hand-off for the editor">
            <Checkbox label="Hand-off" checked={handoff} onChange={e => setHandoff(e.target.checked)} />
          </span>
          <Button variant="primary" data-testid="production-notes-post" disabled={busy || !draft.trim()} loading={busy} onClick={post}>Post</Button>
        </div>
        {error && <span className="ui-field__error" role="alert" data-testid="production-notes-error">{clean(error)}</span>}
      </div>
    </div>
  );
}
